import { Inject, Injectable } from '@angular/core';
import { basketManagerFeatureKey } from '@epicuro-next/constants/features/full-basket-manager';
import { AccountsService } from '@epicuro-next/data/accounts';
import { APP_TOP_LEVEL_PATH } from '@epicuro-next/navigation/constants';
import {
  BROWSER_STORAGE_SERVICE,
  BROWSER_STORAGE_TOKEN,
  SessionStorageService,
} from '@epicuro-next/platform/storage';
import { go } from '@epicuro-next/state/router-store';
import {
  ACCOUNT_ID,
  patchSession,
  selectAccountId as selectSessionAccountId,
} from '@epicuro-next/state/session-state';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { select, Store } from '@ngrx/store';
import { Account } from 'graphql-api/gql-api-interfaces';
import { EMPTY } from 'rxjs';
import { catchError, filter, map, switchMap, take, tap } from 'rxjs/operators';

import {
  clearActiveAccount,
  loadAccountDetailsFailure,
  resolveActiveAccount,
  setActiveAccount,
} from '../actions/accounts.actions';

@Injectable()
export class ActiveAccountEffects {
  public setActiveAccount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(setActiveAccount),
      // set in local storage for old app
      tap(({ account }) =>
        this.storageService.set(ACCOUNT_ID, account.accountId),
      ),
      concatLatestFrom(() => this.store.pipe(select(selectSessionAccountId))),
      filter(
        ([{ account }, sessionAccountId]) =>
          account.accountId !== sessionAccountId,
      ),
      map(([{ account }]) =>
        patchSession({ session: { [ACCOUNT_ID]: account.accountId } }),
      ),
    ),
  );

  public clearBasketManagerFiltersCache$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(setActiveAccount),
        switchMap(() => {
          this.sessionStorageService.remove(
            `${basketManagerFeatureKey}_storage`,
          );
          return EMPTY;
        }),
      ),
    { dispatch: false },
  );

  public resolveActiveAccount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(resolveActiveAccount),
      take(1),
      switchMap(({ accountId }) =>
        this.accountService.getAccountDetails({ accountId }).pipe(
          switchMap((account: Account) => {
            if (account) {
              return [setActiveAccount({ account, isDetailedAccount: true })];
            }

            return [
              clearActiveAccount(),
              go({ commands: [APP_TOP_LEVEL_PATH.accountSelector.path] }),
              patchSession({ session: { [ACCOUNT_ID]: null } }),
            ];
          }),
          catchError((error) => [
            clearActiveAccount(),
            loadAccountDetailsFailure({ error: error.message }),
            go({ commands: [APP_TOP_LEVEL_PATH.accountSelector.path] }),
          ]),
        ),
      ),
    ),
  );

  constructor(
    private actions$: Actions,
    private store: Store,
    private accountService: AccountsService,
    @Inject(BROWSER_STORAGE_TOKEN)
    private storageService: BROWSER_STORAGE_SERVICE,
    private sessionStorageService: SessionStorageService,
  ) {}
}
